import React, { useRef, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";

const DraggableRow = ({
  id,
  index,
  moveRow,
  children,
  isButtonRow = false,
  scrollContainerRef,
}) => {
  const ref = useRef(null);
  const scrollIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  const startAutoScroll = (scrollDirection, container) => {
    if (scrollIntervalRef.current) return;

    const scrollAmount = scrollDirection === "up" ? -15 : 15;

    scrollIntervalRef.current = setInterval(() => {
      if (container) {
        container.scrollTop += scrollAmount;
      }
    }, 16);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleAutoScroll = (clientY, container) => {
    if (!container) {
      stopAutoScroll();
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const scrollThreshold = 40;
    const distanceFromTop = clientY - containerRect.top;
    const distanceFromBottom = containerRect.bottom - clientY;

    if (
      distanceFromTop >= scrollThreshold &&
      distanceFromBottom >= scrollThreshold
    ) {
      stopAutoScroll();
      return;
    }

    if (distanceFromTop < scrollThreshold) {
      startAutoScroll("up", container);
    } else if (distanceFromBottom < scrollThreshold) {
      startAutoScroll("down", container);
    }
  };

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      stopAutoScroll();
    },
  });

  const [, drop] = useDrop({
    accept: "row",
    hover: (item, monitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (scrollContainerRef?.current) {
        handleAutoScroll(clientOffset.y, scrollContainerRef.current);
      }
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: () => {
      stopAutoScroll();
    },
  });

  if (!isButtonRow) {
    drag(drop(ref));
  }

  return (
    <div
      ref={isButtonRow ? null : ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isButtonRow ? "default" : "move",
        padding: "8px 0",
        transform: isDragging ? "rotate(5deg)" : "none",
        transition: isDragging ? "none" : "transform 0.2s ease",
      }}
    >
      {children}
    </div>
  );
};

export default DraggableRow;
